import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { getDataTime } from '@sg/services';
import { getModulesForDownload } from '@sg/_config/dataTimeModules';

type TDurationRange = [string, string];

interface CacheEntry {
  range: TDurationRange | null;
  timestamp: number;
  /** In-flight promise for request deduplication. */
  promise: Promise<TDurationRange | null> | null;
}

// ── Module-level cache ─────────────────────────────────────────────────
// Persists across component unmount/remount and page navigation.
// Keyed by `${scope}:${studioId}` (studioId omitted for portfolio/finance).
const dataTimeCache = new Map<string, CacheEntry>();

// Cache TTL: 1 minute. Short window to balance API load vs data freshness.
const CACHE_TTL = 60 * 1000;

const buildCacheKey = (scope: string, studioId?: string): string =>
  studioId ? `${scope}:${studioId}` : scope;

/** Default range: Jan of selected year → selected month (matches Figma placeholder). */
const buildDefaultDurationRange = (selectedMonth?: string | null): TDurationRange => {
  const endMonth =
    selectedMonth && moment(selectedMonth, 'YYYY-MM').isValid()
      ? moment(selectedMonth, 'YYYY-MM')
      : moment();
  const startMonth = endMonth.clone().startOf('year');
  return [
    startMonth.format('YYYY-MM-DD'),
    endMonth.clone().endOf('month').format('YYYY-MM-DD'),
  ];
};

export { buildDefaultDurationRange };
export type { TDurationRange };

/** Clear the entire data-time cache (e.g., on logout / account switch). */
export function clearDataTimeCache(): void {
  dataTimeCache.clear();
}

/** Invalidate cache for a specific scope + studioId. */
export function invalidateDataTimeCache(scope: string, studioId?: string): void {
  dataTimeCache.delete(buildCacheKey(scope, studioId));
}

interface UseDataTimeRangeOptions {
  visible: boolean;
  scope: 'portfolio' | 'studio' | 'finance';
  studioId?: string;
  selectedMonth?: string | null;
}

/**
 * Fetches and caches the available date range for the metrics download modal.
 *
 * WHY THIS HOOK EXISTS:
 * The MetricsDownloadModal previously called `getDataTime` on every open,
 * causing redundant network requests when users repeatedly opened/closed the
 * modal. This hook introduces a module-level cache with TTL so that the same
 * scope + studioId combination only triggers one API call per TTL window.
 *
 * FEATURES:
 * - Module-level cache: persists across component unmount/remount and page navigation
 * - TTL-based invalidation: cache expires after 1 minute
 * - Request deduplication: simultaneous requests for the same key share one promise
 * - Immediate cache hit: no loading flash when reopening the modal
 * - No caching on network error: transient failures retry on next open
 *
 * @example
 * const { availableDateRange, durationRange, setDurationRange, loading } =
 *   useDataTimeRange({ visible, scope: 'studio', studioId, selectedMonth });
 */
export function useDataTimeRange({
  visible,
  scope,
  studioId,
  selectedMonth,
}: UseDataTimeRangeOptions) {
  const [availableDateRange, setAvailableDateRange] = useState<TDurationRange | null>(null);
  const [durationRange, setDurationRange] = useState<TDurationRange | null>(null);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!visible) {
      // Don't clear cache on close — just reset local display state
      setAvailableDateRange(null);
      setDurationRange(null);
      return;
    }

    const modules = getModulesForDownload(scope);
    if (modules.length === 0) {
      setAvailableDateRange(null);
      setDurationRange(buildDefaultDurationRange(selectedMonth));
      return;
    }

    const cacheKey = buildCacheKey(scope, studioId);
    const now = Date.now();
    const cached = dataTimeCache.get(cacheKey);

    // 1. Valid cache hit — return immediately, no API call
    if (cached && !cached.promise && now - cached.timestamp < CACHE_TTL) {
      setAvailableDateRange(cached.range);
      setDurationRange(cached.range ?? buildDefaultDurationRange(selectedMonth));
      return;
    }

    // 2. In-flight request — reuse it (deduplication)
    if (cached?.promise) {
      setLoading(true);
      cached.promise
        .then((range) => {
          if (!isMountedRef.current) return;
          setAvailableDateRange(range);
          setDurationRange(range ?? buildDefaultDurationRange(selectedMonth));
          setLoading(false);
        })
        .catch(() => {
          if (!isMountedRef.current) return;
          setAvailableDateRange(null);
          setDurationRange(buildDefaultDurationRange(selectedMonth));
          setLoading(false);
        });
      return;
    }

    // 3. Cache miss or expired — fetch new data
    const fetchPromise = (async (): Promise<TDurationRange | null> => {
      try {
        const payload: { modules: string[]; studio_id?: string } = { modules };
        if (scope === 'studio' && studioId) {
          payload.studio_id = studioId;
        }
        const response = await getDataTime(payload);
        if (
          response.code === 0 &&
          response.data?.start_date &&
          response.data?.end_date
        ) {
          return [response.data.start_date, response.data.end_date];
        }
        return null; // API succeeded but no range — cache this
      } catch {
        // Network error — re-throw so caller knows NOT to cache
        throw new Error('NETWORK_ERROR');
      }
    })();

    // Store in-flight promise for deduplication
    dataTimeCache.set(cacheKey, {
      range: cached?.range ?? null,
      timestamp: now,
      promise: fetchPromise,
    });

    setLoading(true);
    fetchPromise
      .then((range) => {
        if (!isMountedRef.current) return;
        // Success — update cache with resolved value
        dataTimeCache.set(cacheKey, {
          range,
          timestamp: Date.now(),
          promise: null,
        });
        setAvailableDateRange(range);
        setDurationRange(range ?? buildDefaultDurationRange(selectedMonth));
        setLoading(false);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        // Network error — clear in-flight promise but DON'T cache the failure
        // so the next open will retry the API
        const entry = dataTimeCache.get(cacheKey);
        if (entry) {
          entry.promise = null;
        }
        setAvailableDateRange(null);
        setDurationRange(buildDefaultDurationRange(selectedMonth));
        setLoading(false);
      });
  }, [visible, scope, studioId, selectedMonth]);

  return {
    availableDateRange,
    durationRange,
    setDurationRange,
    loading,
  };
}
